import { editingStore, isEditingText, hasTextSelection, selectedParagraphs } from './editingStore';

/**
 * Live preview of a line-spacing preset (Home ▸ Line Spacing: hovering / arrowing over 1.0 … 3.0 shows the result
 * before it is picked). Purely visual and DOM-level: the inline styles the real command would write are put on the
 * same elements for the duration of the hover and taken back afterwards — the document, the undo stack and the
 * autosave are never touched. The scope is the command's (useTextCommands.applyParagraphProps): the paragraphs the
 * highlight touches, or the whole box (its paragraphs' own values included) when nothing is highlighted.
 *
 * While a box is edited the editable is previewed; otherwise the display rendering of every selected text box
 * (engine-laid-out boxes — wrap obstacles, Align to Baselines — have no paragraph elements and are skipped).
 */
let saved = []; // [{ el, prop: 'lineHeight', value }] — what to put back

const remember = (el, value) => {
  saved.push({ el, value: el.style.lineHeight });
  el.style.lineHeight = String(value);
};

const PARA_SEL = 'div[style*="line-height"], p[style*="line-height"], li[style*="line-height"]';

/** Preview `value` on the whole text element `root`: the element itself plus every paragraph with its own value. */
const previewWholeBox = (root, value) => {
  remember(root, value);
  root.querySelectorAll(PARA_SEL).forEach((b) => remember(b, value));
};

/** Take the preview back (safe to call any time; elements that have gone are skipped). */
export const endLineSpacingPreview = () => {
  if (!saved.length) return false;
  const list = saved;
  saved = [];
  list.forEach(({ el, value }) => {
    if (!el.isConnected) return;
    if (value) el.style.lineHeight = value;
    else el.style.removeProperty('line-height');
    if (!el.getAttribute('style')) el.removeAttribute('style');
  });
  return true;
};

export const isLineSpacingPreviewing = () => saved.length > 0;

/**
 * Start a preview of `value` (a unitless multiplier). `ids` are the selected text objects (used when nothing is being
 * edited). Returns true when something is being previewed.
 */
export const previewLineSpacing = (value, ids = []) => {
  endLineSpacingPreview();
  if (!(value > 0)) return false;
  if (isEditingText()) {
    const root = editingStore.el;
    if (hasTextSelection()) {
      const blocks = selectedParagraphs({ wrap: false });
      if (blocks.length) { blocks.forEach((b) => remember(b, value)); return true; }
    }
    previewWholeBox(root, value);
    return true;
  }
  let any = false;
  ids.forEach((id) => {
    const root = document.querySelector(`[data-testid="canvas-object-${CSS.escape(id)}"] [data-testid="textbox-display"]`);
    if (!root) return; // engine-laid-out box (or not a text box): no browser-laid-out paragraphs to preview on
    previewWholeBox(root, value);
    any = true;
  });
  return any;
};
